import { type Session, type SupabaseClient, createClient } from "@supabase/supabase-js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  type ReactElement,
  type ReactNode,
  createContext,
  createElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { z } from "zod";

import { type HashAuthError, parseHashAuthError } from "@/lib/auth-hash";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "http://localhost";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "anon";

const initialHash = typeof window !== "undefined" ? window.location.hash : "";

// Captured BEFORE `createClient()` runs, independently of whatever
// `detectSessionInUrl` does to the hash afterward — defensively, not
// because it is known to clear an error hash (in `@supabase/auth-js@2.108.0`
// it does not: `GoTrueClient._getSessionFromURL` throws on an error hash
// before the only `hash = ''` call, which sits in the success branch — but
// this page's behavior should not depend on that library internal across
// versions). `ResetPasswordPage` reads this synchronously to show the
// expired/used message instead of a loading state or a redirect to
// `/login`, regardless of which of the two landings below brought the
// browser here. See
// `openspec/changes/prefetch-safe-auth-email-links/design.md` decision 5.
export const authErrorFromUrl: HashAuthError | null = parseHashAuthError(initialHash);

// Recovery links from Supabase land on `/#access_token=…&type=recovery…`,
// and (design.md decision 5) a Supabase auth error — `error_code=…` or
// `error_description=…`, e.g. an expired/used token — can land the same
// bare-origin way. Rewrite the path to `/reset-password` BEFORE
// `detectSessionInUrl` consumes the hash so the router lands on the reset
// form instead of flashing the chat/login screens while the auth state
// catches up, or (for the error case) silently redirecting to `/login`
// through `AppShell`'s anonymous-session guard.
if (
  typeof window !== "undefined" &&
  window.location.pathname !== "/reset-password" &&
  (initialHash.includes("type=recovery") || authErrorFromUrl !== null)
) {
  window.history.replaceState(null, "", `/reset-password${initialHash}`);
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * A throwaway client for flows that must call `verifyOtp` (or otherwise
 * establish a Supabase session) WITHOUT affecting the app's own signed-in
 * principal — e.g. `/reset-password`'s `token_hash` flow.
 *
 * Verifying on the main `supabase` client above saves the resulting
 * session there and notifies its listeners, which flips
 * `SessionQueryProvider`'s `principalId` (anonymous → the verified user, or
 * one signed-in user → another, if the browser happened to already be
 * signed in as someone else). Because `main.tsx` nests `RouterProvider`
 * inside `SessionQueryProvider`'s `QueryClientProvider`, keyed on
 * `owned.generation`, that principal change remounts the entire routed
 * subtree — including the page that just called `verifyOtp` — while its
 * own submit handler is still awaiting `updateUser`. The orphaned closure's
 * later `setError`/`setSubmitting` then land on an unmounted component: the
 * remounted page silently re-parses the (by then cleared) fragment as
 * "absent" and falls back to an empty legacy form, with no error shown.
 *
 * `persistSession`/`autoRefreshToken`/`detectSessionInUrl` are all off and
 * `storageKey` is distinct so this client never reads or writes the main
 * client's storage and never reacts to the page's own URL. Callers should
 * create one instance per flow (e.g. cached in a `useRef`) rather than a
 * fresh one per call, so a `verifyOtp` success and the `updateUser` call
 * that follows it share the same in-memory session.
 */
export function createEphemeralAuthClient(): SupabaseClient {
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: "beflow-email-link",
    },
  });
}

export const Role = {
  Viewer: "viewer",
  Contributor: "contributor",
  Admin: "admin",
} as const;
export type Role = (typeof Role)[keyof typeof Role];

const appMetadataSchema = z.object({ role: z.enum(Role).default(Role.Viewer) }).passthrough();

/**
 * The role comes from `app_metadata`, which Supabase only accepts from the
 * service-role Admin API — never from `user_metadata`, which the user can
 * write with their own access token (this module's own `theme` preference
 * lives there). Reading the claim from `user_metadata` made every account
 * able to promote itself (SEG #117); see `openspec/specs/security/spec.md`,
 * "Authorization claims are not self-writable". There is deliberately no
 * fallback to `user_metadata`: after migration `0085` the claim is not there,
 * and a fallback would be the vulnerability again.
 *
 * This gates presentation only. The backend and the RLS policies enforce the
 * same claim independently, so a stale role here cannot grant access.
 */
export function roleFromSession(session: Session | null): Role {
  if (!session) return Role.Viewer;
  const parsed = appMetadataSchema.safeParse(session.user.app_metadata);
  return parsed.success ? parsed.data.role : Role.Viewer;
}

export type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; session: Session; role: Role };

interface OwnedQueryState {
  session: SessionState;
  principalId: string | null;
  queryClient: QueryClient;
  generation: number;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

function subscribeToSession(publish: (session: Session | null) => void): () => void {
  let active = true;
  let authEventRevision = 0;
  const initialRevision = authEventRevision;

  const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
    authEventRevision += 1;
    if (active) publish(session);
  });

  void supabase.auth.getSession().then(({ data }) => {
    if (!active || authEventRevision !== initialRevision) return;
    publish(data.session);
  });

  return () => {
    active = false;
    subscription.subscription.unsubscribe();
  };
}

/** Owns one private query cache per authenticated principal. */
export function SessionQueryProvider({ children }: { children: ReactNode }): ReactElement {
  const [owned, setOwned] = useState<OwnedQueryState>(() => ({
    session: { status: "loading" },
    principalId: null,
    queryClient: createQueryClient(),
    generation: 0,
  }));
  const ownedRef = useRef(owned);
  const transitionQueueRef = useRef(Promise.resolve());

  useEffect(
    () =>
      subscribeToSession((session) => {
        const nextSession = toState(session);
        const nextPrincipalId = session?.user.id ?? null;
        transitionQueueRef.current = transitionQueueRef.current.then(async () => {
          const current = ownedRef.current;
          if (current.principalId === nextPrincipalId) {
            const next = { ...current, session: nextSession };
            ownedRef.current = next;
            setOwned(next);
            return;
          }

          await current.queryClient.cancelQueries();
          current.queryClient.clear();
          const next: OwnedQueryState = {
            session: nextSession,
            principalId: nextPrincipalId,
            queryClient: createQueryClient(),
            generation: current.generation + 1,
          };
          ownedRef.current = next;
          setOwned(next);
        });
      }),
    [],
  );

  return createElement(
    SessionContext.Provider,
    { value: owned.session },
    createElement(
      QueryClientProvider,
      { client: owned.queryClient, key: owned.generation },
      children,
    ),
  );
}

export function useSession(): SessionState {
  const provided = useContext(SessionContext);
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    if (provided !== undefined) return;
    return subscribeToSession((session) => setState(toState(session)));
  }, [provided]);

  return provided ?? state;
}

function toState(session: Session | null): SessionState {
  if (!session) return { status: "anonymous" };
  return { status: "authenticated", session, role: roleFromSession(session) };
}
